import React from "react";

/**
 * Rich, industry-appropriate dashboard preview mockups shown on product cards.
 * Each returns a static SVG-styled card mimic. Designed to fit inside a 16/10 box.
 */

const shellCls = "w-full h-full bg-white text-[color:var(--ink)] p-3 flex flex-col gap-2 relative overflow-hidden";
const dotRow = (
  <div className="flex items-center gap-1 mb-1">
    <span className="w-1.5 h-1.5 rounded-full bg-neutral-300" />
    <span className="w-1.5 h-1.5 rounded-full bg-neutral-300" />
    <span className="w-1.5 h-1.5 rounded-full bg-neutral-300" />
    <span className="ml-auto w-6 h-1.5 bg-[color:var(--brand)]" />
  </div>
);

function Card({ children }) {
  return <div className={shellCls}>{dotRow}{children}</div>;
}

// Bank
function Bank() {
  return (
    <Card>
      <div className="grid grid-cols-3 gap-1.5 text-[9px]">
        <div className="border border-neutral-200 p-1.5">
          <div className="font-mono text-[7px] tracking-widest text-neutral-500">AUTO-MATCH RATE</div>
          <div className="font-display font-black text-lg leading-none mt-1">96.8%</div>
          <div className="h-1 mt-1 bg-emerald-500/70" />
        </div>
        <div className="border border-neutral-200 p-1.5">
          <div className="font-mono text-[7px] tracking-widest text-neutral-500">MATCHED TODAY</div>
          <div className="font-display font-black text-lg leading-none mt-1">1,247</div>
          <div className="h-1 mt-1 bg-[color:var(--brand)]/60" />
        </div>
        <div className="border border-neutral-200 p-1.5">
          <div className="font-mono text-[7px] tracking-widest text-neutral-500">OPEN EXCEPTIONS</div>
          <div className="font-display font-black text-lg text-[color:var(--brand)] leading-none mt-1">14</div>
          <div className="h-1 mt-1 bg-amber-400" />
        </div>
      </div>
      <div className="border-t border-neutral-200 pt-1.5 flex-1">
        <div className="font-mono text-[8px] tracking-widest text-neutral-500">NOSTRO · USD CORRESPONDENT</div>
        <div className="mt-1 space-y-1">
          {[[30,28],[70,12],[60,16],[80,8]].map(([a,b], i) => (
            <div key={i} className="flex items-center gap-1">
              <div className="h-1 bg-neutral-200" style={{ width: `${a}%` }} />
              <div className="h-1 flex-1 bg-emerald-500/60" />
              <div className="h-1 bg-[color:var(--brand)]/70" style={{ width: `${b}%` }} />
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function Hotel() {
  return (
    <Card>
      <div className="flex items-center justify-between text-[9px]">
        <span className="font-mono tracking-widest text-neutral-600">FRONT DESK · TODAY</span>
        <span className="font-mono text-emerald-700">62% occupancy</span>
      </div>
      <div className="grid grid-cols-6 gap-1 flex-1 content-start">
        {[101,102,103,104,105,106,201,202,203,204,205,206].map((n, i) => {
          const status = [true,true,false,true,true,true,true,false,true,true,true,true][i];
          return (
            <div key={n} className={`h-6 flex items-center justify-center font-mono text-[9px] ${status ? "bg-[color:var(--brand)] text-white" : "bg-neutral-100 text-neutral-500"}`}>{n}</div>
          );
        })}
      </div>
      <div className="border-t border-neutral-200 pt-1.5 flex items-center justify-between">
        <span className="font-mono text-[8px] tracking-widest text-neutral-500">ARRIVALS</span>
        <span className="bg-[color:var(--brand)] text-white font-mono text-[8px] px-1.5 py-0.5">CHECK-IN 12:30 PM</span>
      </div>
    </Card>
  );
}

function Hospital() {
  return (
    <Card>
      <div className="grid grid-cols-2 gap-2 flex-1">
        <div className="border border-neutral-200 p-1.5">
          <div className="font-mono text-[7px] tracking-widest text-neutral-500">OPD QUEUE · DR. RAHMAN</div>
          <div className="mt-1 flex items-center gap-1">
            <span className="w-4 h-4 bg-[color:var(--brand)] text-white font-mono text-[8px] flex items-center justify-center rounded-full">7</span>
            <span className="font-mono text-[8px] text-neutral-500">NOW</span>
          </div>
          <div className="mt-1.5 space-y-0.5">
            {[1,2,3].map(i => <div key={i} className="h-1 bg-neutral-200" />)}
          </div>
        </div>
        <div className="border border-neutral-200 p-1.5">
          <div className="font-mono text-[7px] tracking-widest text-neutral-500">PATIENT VITALS</div>
          <svg viewBox="0 0 100 40" className="mt-1">
            <polyline points="0,25 10,15 20,28 30,10 40,22 50,8 60,26 70,12 80,20 90,5 100,18" fill="none" stroke="#1E88F0" strokeWidth="1.5" />
          </svg>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="border border-neutral-200 p-1.5">
          <div className="font-display font-black text-base leading-none">64</div>
          <div className="font-mono text-[7px] tracking-widest text-neutral-500 mt-0.5">IPD BEDS OCCUPIED</div>
        </div>
        <div className="border border-neutral-200 p-1.5">
          <div className="font-display font-black text-base text-emerald-700 leading-none">₹2.4L</div>
          <div className="font-mono text-[7px] tracking-widest text-neutral-500 mt-0.5">PHARMACY SALES TODAY</div>
        </div>
      </div>
    </Card>
  );
}

function School() {
  return (
    <Card>
      <div className="grid grid-cols-2 gap-2 flex-1">
        <div className="border border-neutral-200 p-1.5">
          <div className="font-mono text-[7px] tracking-widest text-neutral-500">FEE COLLECTION · THIS TERM</div>
          <div className="flex items-end gap-0.5 h-10 mt-1">
            {[6,7,4,9,3].map((h,i) => (
              <div key={i} className={`flex-1 ${i===3?"bg-[color:var(--brand)]":i===4?"bg-amber-400":"bg-[color:var(--brand)]/80"}`} style={{ height: `${h*10}%` }} />
            ))}
          </div>
        </div>
        <div className="border border-neutral-200 p-1.5 flex flex-col items-center justify-center">
          <div className="font-mono text-[7px] tracking-widest text-neutral-500">ATTENDANCE</div>
          <svg viewBox="0 0 40 40" className="mt-1 w-10 h-10">
            <circle cx="20" cy="20" r="16" fill="none" stroke="#e5e5e5" strokeWidth="4" />
            <circle cx="20" cy="20" r="16" fill="none" stroke="#10b981" strokeWidth="4" strokeDasharray="92 100" strokeDashoffset="25" transform="rotate(-90 20 20)" />
          </svg>
          <div className="font-display font-bold text-[10px] mt-0.5">92% TODAY</div>
        </div>
      </div>
      <div className="border border-neutral-200 p-1.5">
        <div className="font-mono text-[7px] tracking-widest text-neutral-500">PARENT APP</div>
        <div className="mt-1 space-y-0.5">
          <div className="bg-[color:var(--brand)]/10 text-[8px] px-1.5 py-0.5">Exam timetable sent ✓</div>
          <div className="bg-neutral-100 text-[8px] px-1.5 py-0.5">Bus arriving · 7:42 AM</div>
        </div>
      </div>
    </Card>
  );
}

function MLM() {
  return (
    <Card>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[7px] tracking-widest text-neutral-500">BINARY GENEALOGY</span>
        <div className="border border-neutral-200 px-1.5 py-0.5">
          <div className="font-mono text-[6px] text-neutral-500">E-WALLET</div>
          <div className="font-display font-black text-[10px]">₹48,250</div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center relative">
        <svg viewBox="0 0 120 60" className="w-full h-full">
          <line x1="60" y1="14" x2="30" y2="42" stroke="#1e88f0" strokeWidth="1" />
          <line x1="60" y1="14" x2="90" y2="42" stroke="#1e88f0" strokeWidth="1" />
          <line x1="30" y1="42" x2="15" y2="55" stroke="#1e88f0" strokeWidth="1" />
          <line x1="30" y1="42" x2="45" y2="55" stroke="#1e88f0" strokeWidth="1" />
          <line x1="90" y1="42" x2="75" y2="55" stroke="#1e88f0" strokeWidth="1" />
          <line x1="90" y1="42" x2="105" y2="55" stroke="#1e88f0" strokeWidth="1" />
          <circle cx="60" cy="14" r="6" fill="#1e88f0" />
          <text x="60" y="16" textAnchor="middle" fill="white" fontSize="4" fontFamily="monospace">YOU</text>
          <circle cx="30" cy="42" r="4" fill="#1e88f0" />
          <circle cx="90" cy="42" r="4" fill="#1e88f0" />
          <circle cx="15" cy="55" r="3" fill="#e5e5e5" />
          <circle cx="45" cy="55" r="3" fill="#e5e5e5" />
          <circle cx="75" cy="55" r="3" fill="#e5e5e5" />
          <circle cx="105" cy="55" r="3" fill="#10b981" />
        </svg>
      </div>
      <div className="border-t border-neutral-200 pt-1 font-mono text-[7px] flex justify-between text-neutral-600">
        <span>MATCHING BONUS · L 1,240 · R 1,180</span>
        <span className="text-emerald-700">Paid 2:1</span>
      </div>
    </Card>
  );
}

function Home() {
  return (
    <Card>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[7px] tracking-widest text-neutral-500">LIVING ROOM</span>
        <span className="font-mono text-[7px] text-neutral-400">MAJLIS</span>
      </div>
      <div className="grid grid-cols-2 gap-2 flex-1">
        <div className="border border-neutral-200 p-1.5 flex flex-col items-center justify-center">
          <svg viewBox="0 0 40 40" className="w-10 h-10">
            <circle cx="20" cy="20" r="16" fill="none" stroke="#e5e5e5" strokeWidth="3" />
            <circle cx="20" cy="20" r="16" fill="none" stroke="#1e88f0" strokeWidth="3" strokeDasharray="60 100" transform="rotate(-90 20 20)" />
          </svg>
          <div className="font-display font-black text-sm mt-1">22°</div>
          <div className="font-mono text-[7px] text-neutral-500">COOLING</div>
        </div>
        <div className="space-y-1">
          {[["Lights · Majlis", true], ["Curtains · Bedroom", false], ["Main door · Locked", true]].map(([label, on]) => (
            <div key={label} className="flex items-center justify-between border border-neutral-200 px-1.5 py-1">
              <span className="text-[8px]">{label}</span>
              <span className={`w-4 h-2 ${on ? "bg-[color:var(--brand)]" : "bg-neutral-200"} relative`}>
                <span className={`absolute top-0 ${on ? "right-0" : "left-0"} w-2 h-2 bg-white`} />
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-neutral-200 pt-1 flex items-center gap-1 font-mono text-[7px]">
        <span className="text-neutral-500">SCENE</span>
        <span className="bg-[color:var(--brand)]/20 text-[color:var(--brand)] px-1.5">EVENING</span>
        <span className="bg-neutral-100 px-1.5">AWAY</span>
        <span className="bg-neutral-100 px-1.5">PRAYER</span>
      </div>
    </Card>
  );
}

function LMS() {
  return (
    <Card>
      <div className="grid grid-cols-2 gap-2 flex-1">
        <div className="border border-neutral-200 p-1.5">
          <div className="font-mono text-[7px] tracking-widest text-neutral-500">MODULE 4 · LIVE CLASS RECORDING</div>
          <div className="mt-1 bg-neutral-900 aspect-video relative flex items-center justify-center">
            <div className="w-4 h-4 bg-[color:var(--brand)] rounded-full flex items-center justify-center">
              <div className="w-0 h-0 border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent border-l-[5px] border-l-white ml-0.5" />
            </div>
          </div>
          <div className="h-0.5 mt-1 bg-neutral-200"><div className="h-0.5 bg-[color:var(--brand)] w-2/3" /></div>
        </div>
        <div className="space-y-1.5">
          <div className="border border-neutral-200 p-1.5">
            <div className="font-mono text-[7px] tracking-widest text-neutral-500">QUIZ · PROCTORED</div>
            <div className="font-display font-black text-sm mt-0.5">18/20</div>
            <div className="bg-emerald-100 text-emerald-700 text-[7px] mt-0.5 px-1">PASSED ✓</div>
          </div>
          <div className="border border-neutral-200 p-1.5">
            <div className="font-mono text-[7px] tracking-widest text-neutral-500">ENROLLED</div>
            <div className="font-display font-black text-sm">2,340</div>
            <div className="font-mono text-[7px] text-neutral-500">LEARNERS · 14 COURSES</div>
          </div>
        </div>
      </div>
      <div className="border-t border-neutral-200 pt-1 flex items-center gap-1">
        <span className="font-mono text-[7px] tracking-widest text-neutral-500 flex-1">COURSE PROGRESS</span>
        <div className="h-0.5 flex-1 bg-neutral-200"><div className="h-0.5 bg-emerald-500 w-2/3" /></div>
        <span className="font-mono text-[7px]">68%</span>
      </div>
    </Card>
  );
}

function Manufacturing() {
  return (
    <Card>
      <div className="grid grid-cols-2 gap-2 flex-1">
        <div className="border border-neutral-200 p-1.5">
          <div className="font-mono text-[7px] tracking-widest text-neutral-500">PRODUCTION ORDERS</div>
          <div className="mt-1 space-y-0.5">
            <div className="flex items-center justify-between text-[8px]"><span className="bg-emerald-100 text-emerald-700 px-1">COMPLETED</span><span className="font-mono">6</span></div>
            <div className="flex items-center justify-between text-[8px]"><span className="bg-[color:var(--brand)]/15 text-[color:var(--brand)] px-1">IN PROGRESS</span><span className="font-mono">12</span></div>
            <div className="flex items-center justify-between text-[8px]"><span className="bg-amber-100 text-amber-700 px-1">MAT. SHORT</span><span className="font-mono">2</span></div>
            <div className="flex items-center justify-between text-[8px]"><span className="bg-neutral-100 px-1">PLANNED</span><span className="font-mono">18</span></div>
          </div>
        </div>
        <div className="border border-neutral-200 p-1.5 flex flex-col items-center justify-center">
          <div className="font-mono text-[7px] tracking-widest text-neutral-500">OEE</div>
          <svg viewBox="0 0 40 40" className="w-10 h-10 mt-1">
            <circle cx="20" cy="20" r="15" fill="none" stroke="#e5e5e5" strokeWidth="4" />
            <circle cx="20" cy="20" r="15" fill="none" stroke="#1e88f0" strokeWidth="4" strokeDasharray="81 100" transform="rotate(-90 20 20)" />
          </svg>
          <div className="font-display font-black text-xs">81%</div>
          <div className="font-mono text-[7px] text-neutral-500">PLANT 1</div>
        </div>
      </div>
      <div className="border border-neutral-200 p-1.5">
        <div className="font-mono text-[7px] tracking-widest text-neutral-500">STOCK ALERTS</div>
        <div className="mt-1 flex flex-col gap-0.5">
          <div className="text-[8px] bg-[color:var(--brand)]/10 text-[color:var(--brand)] px-1">Steel coil · below ROL</div>
          <div className="text-[8px] bg-neutral-100 px-1">e-invoice batch sent ✓</div>
        </div>
      </div>
    </Card>
  );
}

function Restaurant() {
  return (
    <Card>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[7px] tracking-widest text-neutral-500">TABLES · DINNER SERVICE</span>
        <div className="border border-neutral-200 px-1.5 py-0.5"><div className="font-mono text-[6px] text-neutral-500">KOT · TABLE 2</div></div>
      </div>
      <div className="grid grid-cols-6 gap-1 flex-1 content-start">
        {[["T1","brand"],["T2","brand"],["T3","empty"],["T4","amber"],["T5","brand"],["T6","empty"]].map(([n, kind], i) => (
          <div key={i} className={`aspect-square rounded-full flex items-center justify-center font-mono text-[8px] font-bold ${kind==="brand"?"bg-[color:var(--brand)] text-white":kind==="amber"?"bg-amber-400 text-white":"bg-neutral-100 text-neutral-500"}`}>{n}</div>
        ))}
      </div>
      <div className="border border-neutral-200 p-1 space-y-0.5">
        <div className="flex justify-between text-[7px]"><span>2 × Chicken Machboos</span><span className="font-mono">64.00</span></div>
        <div className="flex justify-between text-[7px]"><span>1 × Hummus & Bread</span><span className="font-mono">22.00</span></div>
        <div className="flex justify-between text-[7px]"><span>3 × Fresh Lime Mint</span><span className="font-mono">36.00</span></div>
        <div className="flex justify-between text-[8px] font-bold pt-0.5 border-t border-neutral-200"><span>TOTAL · AED</span><span>122.00</span></div>
      </div>
      <div className="flex gap-1">
        <span className="flex-1 bg-[color:var(--brand)] text-white text-[7px] font-bold text-center py-0.5">SEND KOT</span>
        <span className="flex-1 bg-neutral-100 text-[7px] font-bold text-center py-0.5">BILL</span>
      </div>
    </Card>
  );
}

function Property() {
  return (
    <Card>
      <div className="grid grid-cols-3 gap-1.5 flex-1">
        {["Marina Tower · 2BR", "JLT Office · 1,400 ft²"].map((label, i) => (
          <div key={i} className="border border-neutral-200 p-1">
            <div className="aspect-video bg-neutral-100 flex items-center justify-center">
              <svg viewBox="0 0 40 20" className="w-3/4 h-3/4"><polygon points="20,4 4,16 36,16" fill="#e5e5e5" /></svg>
            </div>
            <div className="text-[7px] mt-1 truncate">{label}</div>
            <div className="text-[6px] bg-emerald-100 text-emerald-700 px-1 mt-0.5 inline-block">{i===0?"LEASED ✓":"VIEWING"}</div>
          </div>
        ))}
        <div className="border border-neutral-200 p-1.5">
          <div className="font-mono text-[7px] tracking-widest text-neutral-500">RENT COLLECTED</div>
          <div className="font-display font-black text-lg mt-0.5">94%</div>
          <div className="h-0.5 mt-0.5 bg-neutral-200"><div className="h-0.5 bg-emerald-500 w-[94%]" /></div>
          <div className="text-[7px] text-neutral-500 mt-1">3 cheques due this week</div>
        </div>
      </div>
      <div className="border-t border-neutral-200 pt-1">
        <div className="font-mono text-[7px] tracking-widest text-neutral-500">MAINTENANCE TICKETS</div>
        <div className="mt-0.5 flex gap-1">
          <span className="bg-[color:var(--brand)]/10 text-[color:var(--brand)] text-[7px] px-1">AC fault · Unit 1204 · URGENT</span>
          <span className="bg-neutral-100 text-[7px] px-1">Plumbing · Villa 8 · Assigned</span>
        </div>
      </div>
    </Card>
  );
}

function HR() {
  return (
    <Card>
      <div className="grid grid-cols-2 gap-2 flex-1">
        <div className="border border-neutral-200 p-1.5">
          <div className="font-mono text-[7px] tracking-widest text-neutral-500">PAYROLL RUN · JUNE 2026</div>
          <div className="mt-1 space-y-0.5">
            {[["","AED 8,600","80%"],["","AED 12,200","100%"],["","AED 6,760","55%"]].map(([_, v, w], i) => (
              <div key={i} className="flex items-center gap-1">
                <div className="h-1 flex-1 bg-neutral-200"><div className="h-1 bg-[color:var(--brand)]" style={{ width: w }} /></div>
                <span className="font-mono text-[7px]">{v}</span>
              </div>
            ))}
          </div>
          <div className="mt-1 flex items-center gap-1">
            <span className="bg-[color:var(--brand)] text-white text-[7px] px-1">GENERATE WPS SIF FILE</span>
            <span className="font-mono text-[7px] text-emerald-700">42/42 PAID ✓</span>
          </div>
        </div>
        <div className="border border-neutral-200 p-1.5 space-y-1">
          <div>
            <div className="font-mono text-[7px] tracking-widest text-neutral-500">HEADCOUNT</div>
            <div className="font-display font-black text-lg">42</div>
            <div className="font-mono text-[7px] text-[color:var(--brand)]">3 visas expiring soon</div>
          </div>
          <div>
            <div className="font-mono text-[7px] tracking-widest text-neutral-500">LEAVE REQUESTS</div>
            <div className="text-[7px] bg-amber-100 text-amber-700 px-1 inline-block">Annual · 5 days · Pending</div>
            <div className="text-[7px] bg-emerald-100 text-emerald-700 px-1 inline-block mt-0.5">Sick · 1 day · Approved ✓</div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function Logistics() {
  return (
    <Card>
      <div className="grid grid-cols-2 gap-2 flex-1">
        <div className="border border-neutral-200 p-1.5 relative">
          <div className="font-mono text-[7px] tracking-widest text-neutral-500 mb-1">ROUTE · TRK-07 · ON TIME</div>
          <svg viewBox="0 0 100 60" className="w-full h-16">
            <circle cx="15" cy="45" r="3" fill="#050505" />
            <text x="20" y="47" fontSize="4" fontFamily="monospace">JEBEL ALI</text>
            <path d="M15 45 Q40 20 55 30 T85 15" stroke="#1e88f0" strokeWidth="1.5" fill="none" strokeDasharray="2 1" />
            <circle cx="55" cy="30" r="2.5" fill="#1e88f0" />
            <circle cx="85" cy="15" r="3" fill="none" stroke="#050505" strokeWidth="1" />
            <text x="60" y="18" fontSize="4" fontFamily="monospace">ABU DHABI DEPOT</text>
          </svg>
        </div>
        <div className="border border-neutral-200 p-1.5 space-y-1">
          <div>
            <div className="font-mono text-[7px] tracking-widest text-neutral-500">SHIPMENTS TODAY</div>
            <div className="font-display font-black text-lg">128</div>
            <div className="font-mono text-[7px] text-emerald-700">96% DELIVERED ON TIME</div>
          </div>
          <div>
            <div className="font-mono text-[7px] tracking-widest text-neutral-500">FLEET</div>
            <div className="text-[8px]">18 active · <span className="text-[color:var(--brand)]">2 in service</span></div>
            <div className="text-[7px] bg-amber-100 text-amber-700 px-1 inline-block">TRK-11 · Insurance due</div>
          </div>
        </div>
      </div>
    </Card>
  );
}

const REGISTRY = {
  bank: Bank, hotel: Hotel, hospital: Hospital, school: School, mlm: MLM, home: Home,
  lms: LMS, manufacturing: Manufacturing, restaurant: Restaurant, property: Property, hr: HR, logistics: Logistics,
};

export default function ProductPreview({ preview_key = "bank" }) {
  const C = REGISTRY[preview_key] || Bank;
  return <C />;
}
